import {
  Badge,
  Box,
  Divider,
  Flex,
  Grid,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { FC, useCallback, useEffect, useState } from 'react'
import { BsCalculator } from 'react-icons/bs'
import { FiTrendingUp } from 'react-icons/fi'
import {
  MdOutlineCalendarToday,
  MdOutlineFilterAlt,
  MdOutlineInsights,
  MdOutlineShoppingCart
} from 'react-icons/md'

import mrp from '@service/mrp'

// ── Interfaces ───────────────────────────────────────────────────────────────

interface CalculoDetalheTabsModalProps {
  isOpen: boolean
  onClose: () => void
  produtoId: string
  lojaId: string
  etapasSaida: string[]
  etapasQuantidade: string[]
  defaultTab: 'vmm' | 'lambda'
  showVmmTab: boolean
}

interface IPasso {
  ordem: number
  chave: string
  titulo: string
  descricao: string
  formula?: string
  resultado: string
}

interface IVmmDetalhe {
  produtoId: number
  lojaId: number
  metodo: string
  periodo: {
    dataInicial: string
    dataFinal: string
    janelaEfetivaDeDias: number
    primeiraEntradaDoProduto: string
    diasComVenda: number
    diasSemVenda: number
  }
  corte: {
    percentil: number
    minimoDiasComVenda: number
    guardAtingido: boolean
    limiteOutlier: number
    diasExcluidos: number
    volumeExcluido: number
    redeSegurancaAcionada: boolean
  }
  totais: {
    totalVendasBruto: number
    totalVendasLiquido: number
    vendaMediaDiaria: number
  }
  memoriaCalculo: {
    resumo: string
    passos: IPasso[]
  }
  dias: {
    produtoId: number
    lojaId: number
    data: string
    quantidade: number
    situacao: string
  }[]
}

// ── Constants ────────────────────────────────────────────────────────────────

const BRAND_GREEN = '#1d5840'
const BRAND_YELLOW = '#ECC94B'

// ── Animations ───────────────────────────────────────────────────────────────

const listVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07, delayChildren: 0.05 }
  }
}

const cardVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.22, ease: 'easeOut' }
  }
}

const headerVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.2, ease: 'easeOut' } }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  if (!iso) return '-'
  const [datePart] = iso.split('T')
  const [year, month, day] = datePart.split('-')
  return `${day}/${month}/${year}`
}

const formatNumber = (n: number, decimals = 2) =>
  n != null
    ? n.toLocaleString('pt-BR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      })
    : '-'

// ── Lambda StepCard (strings) ────────────────────────────────────────────────

const LambdaStepCard: FC<{
  step: string
  index: number
  total: number
  colorScheme: 'blue' | 'green'
}> = ({ step, index, total, colorScheme }) => {
  const isLast = index === total - 1

  const colors = {
    blue: {
      bg: isLast ? '#EBF8FF' : 'white',
      border: isLast ? '#3182CE' : '#E2E8F0',
      badgeBg: isLast ? '#3182CE' : '#EBF8FF',
      badgeColor: isLast ? 'white' : '#3182CE',
      textColor: isLast ? '#1A365D' : '#2D3748'
    },
    green: {
      bg: isLast ? '#F0FFF4' : 'white',
      border: isLast ? BRAND_GREEN : '#E2E8F0',
      badgeBg: isLast ? BRAND_GREEN : '#F0FFF4',
      badgeColor: isLast ? 'white' : BRAND_GREEN,
      textColor: isLast ? '#1C4532' : '#2D3748'
    }
  }
  const c = colors[colorScheme]

  return (
    <motion.div variants={cardVariants}>
      <Flex
        align="flex-start"
        gap={5}
        bg={c.bg}
        border="1px solid"
        borderColor={c.border}
        borderRadius="10px"
        px={5}
        py={3}
        boxShadow={isLast ? 'sm' : 'none'}
      >
        <Flex
          flexShrink={0}
          w="28px"
          h="28px"
          borderRadius="full"
          bg={c.badgeBg}
          color={c.badgeColor}
          align="center"
          justify="center"
          fontSize="11px"
          fontWeight="800"
          border="1px solid"
          borderColor={c.border}
          mt="1px"
        >
          {isLast ? '✓' : index + 1}
        </Flex>

        <Text
          ml={2}
          fontSize="12.5px"
          color={c.textColor}
          fontWeight={isLast ? '700' : '400'}
          lineHeight="1.6"
          flex={1}
        >
          {step}
        </Text>

        {isLast && (
          <Badge
            colorScheme={colorScheme === 'blue' ? 'blue' : 'green'}
            variant="solid"
            fontSize="9px"
            px={2}
            py={0.5}
            borderRadius="full"
            flexShrink={0}
            alignSelf="center"
          >
            RESULTADO
          </Badge>
        )}
      </Flex>
    </motion.div>
  )
}

const LambdaStepList: FC<{
  steps: string[]
  colorScheme: 'blue' | 'green'
  animationKey: string
}> = ({ steps, colorScheme, animationKey }) => (
  <AnimatePresence>
    <motion.div
      key={animationKey}
      variants={listVariants}
      initial="hidden"
      animate="visible"
    >
      <VStack spacing={2} align="stretch">
        {steps.length > 0 ? (
          steps.map((step, i) => (
            <LambdaStepCard
              key={i}
              step={step}
              index={i}
              total={steps.length}
              colorScheme={colorScheme}
            />
          ))
        ) : (
          <Flex
            align="center"
            justify="center"
            h="80px"
            borderRadius="10px"
            border="1px dashed"
            borderColor="gray.200"
            bg="gray.50"
          >
            <Text fontSize="12px" color="gray.400" fontStyle="italic">
              Sem etapas disponíveis para esta linha
            </Text>
          </Flex>
        )}
      </VStack>
    </motion.div>
  </AnimatePresence>
)

// ── VMM StepCard (passos com titulo/descricao/formula) ───────────────────────

const VmmStepCard: FC<{
  passo: IPasso
  index: number
  total: number
}> = ({ passo, index, total }) => {
  const isLast = index === total - 1

  const bg = isLast ? '#EBF8FF' : 'white'
  const border = isLast ? '#3182CE' : '#E2E8F0'
  const badgeBg = isLast ? '#3182CE' : '#EBF8FF'
  const badgeColor = isLast ? 'white' : '#3182CE'
  const textColor = isLast ? '#1A365D' : '#2D3748'

  return (
    <motion.div variants={cardVariants}>
      <Flex
        align="flex-start"
        gap={5}
        bg={bg}
        border="1px solid"
        borderColor={border}
        borderRadius="10px"
        px={5}
        py={3}
        boxShadow={isLast ? 'sm' : 'none'}
      >
        <Flex
          flexShrink={0}
          w="28px"
          h="28px"
          borderRadius="full"
          bg={badgeBg}
          color={badgeColor}
          align="center"
          justify="center"
          fontSize="11px"
          fontWeight="800"
          border="1px solid"
          borderColor={border}
          mt="1px"
        >
          {isLast ? '✓' : passo.ordem}
        </Flex>

        <Box flex={1}>
          <Text
            fontSize="12.5px"
            fontWeight="700"
            color={textColor}
            lineHeight="1.4"
          >
            {passo.titulo}
          </Text>
          <Text fontSize="12px" color="gray.600" lineHeight="1.6" mt={1}>
            {passo.descricao}
          </Text>
          {passo.formula && (
            <Text fontSize="11px" color="gray.400" fontStyle="italic" mt={1}>
              {passo.formula}
            </Text>
          )}
          <Badge colorScheme="blue" variant="subtle" fontSize="10px" mt={1}>
            {passo.resultado}
          </Badge>
        </Box>

        {isLast && (
          <Badge
            colorScheme="blue"
            variant="solid"
            fontSize="9px"
            px={2}
            py={0.5}
            borderRadius="full"
            flexShrink={0}
            alignSelf="center"
          >
            RESULTADO
          </Badge>
        )}
      </Flex>
    </motion.div>
  )
}

// ── StatCard ─────────────────────────────────────────────────────────────────

const StatCard: FC<{
  label: string
  value: string
  color?: string
}> = ({ label, value, color = 'gray.800' }) => (
  <motion.div variants={cardVariants}>
    <Stat
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="10px"
      px={4}
      py={3}
    >
      <StatLabel fontSize="11px" color="gray.500">
        {label}
      </StatLabel>
      <StatNumber fontSize="16px" color={color} fontWeight="700">
        {value}
      </StatNumber>
    </Stat>
  </motion.div>
)

// ── Tab: Lambda ──────────────────────────────────────────────────────────────

const LambdaTabContent: FC<{
  etapasSaida: string[]
  etapasQuantidade: string[]
  animKey: string
}> = ({ etapasSaida, etapasQuantidade, animKey }) => (
  <Grid templateColumns={['1fr', '1fr', '1fr 1px 1fr']} gap={0}>
    {/* Saída Média Diária */}
    <Box pr={[0, 0, 5]} pb={[6, 6, 0]}>
      <motion.div
        key={`header-saida-${animKey}`}
        variants={headerVariants}
        initial="hidden"
        animate="visible"
      >
        <Flex align="center" gap={3} mb={4}>
          <Flex
            w="40px"
            h="40px"
            borderRadius="10px"
            bg="#EBF8FF"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon as={FiTrendingUp} color="#3182CE" boxSize={5} />
          </Flex>
          <Box>
            <Heading fontSize="14px" fontWeight="700" color="gray.800">
              Saída Média Diária
            </Heading>
            <Text fontSize="11px" color="gray.500" mt="1px">
              Como o valor de venda média diária foi calculado
            </Text>
          </Box>
        </Flex>
      </motion.div>

      <LambdaStepList
        steps={etapasSaida}
        colorScheme="blue"
        animationKey={`saida-${animKey}`}
      />
    </Box>

    {/* Divider */}
    <Box display={['none', 'none', 'block']}>
      <Divider orientation="vertical" borderColor="gray.200" />
    </Box>
    <Divider display={['block', 'block', 'none']} mb={6} />

    {/* Quantidade Recomendada */}
    <Box pl={[0, 0, 5]}>
      <motion.div
        key={`header-qtd-${animKey}`}
        variants={headerVariants}
        initial="hidden"
        animate="visible"
        style={{ transitionDelay: '0.05s' }}
      >
        <Flex align="center" gap={3} mb={4}>
          <Flex
            w="40px"
            h="40px"
            borderRadius="10px"
            bg="#F0FFF4"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon as={MdOutlineShoppingCart} color={BRAND_GREEN} boxSize={5} />
          </Flex>
          <Box>
            <Heading fontSize="14px" fontWeight="700" color="gray.800">
              Quantidade Recomendada
            </Heading>
            <Text fontSize="11px" color="gray.500" mt="1px">
              Como o valor final de recomendação foi obtido
            </Text>
          </Box>
        </Flex>
      </motion.div>

      <LambdaStepList
        steps={etapasQuantidade}
        colorScheme="green"
        animationKey={`qtd-${animKey}`}
      />
    </Box>
  </Grid>
)

// ── Tab: Venda Média Móvel ───────────────────────────────────────────────────

const VmmTabContent: FC<{
  produtoId: string
  lojaId: string
  isActive: boolean
  animKey: string
}> = ({ produtoId, lojaId, isActive, animKey }) => {
  const [data, setData] = useState<IVmmDetalhe | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fetched, setFetched] = useState(false)

  const fetchData = useCallback(async () => {
    if (!produtoId || !lojaId) return
    setLoading(true)
    setError('')
    setData(null)
    try {
      const res = await mrp.getVendaMediaMovelDetalhe(
        Number(produtoId),
        Number(lojaId)
      )
      if (res.data?.success && res.data?.data) {
        setData(res.data.data)
      } else {
        setError(res.data?.message || 'Não foi possível obter os dados.')
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        'Erro ao buscar detalhamento da Venda Média Móvel.'
      setError(msg)
    } finally {
      setLoading(false)
      setFetched(true)
    }
  }, [produtoId, lojaId])

  useEffect(() => {
    if (isActive && !fetched) fetchData()
  }, [isActive, fetched, fetchData])

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="lg" color={BRAND_GREEN} />
      </Flex>
    )
  }

  if (error) {
    return (
      <Flex
        justify="center"
        align="center"
        minH="200px"
        direction="column"
        gap={2}
      >
        <Text color="red.500" fontWeight="600">
          {error}
        </Text>
        <Text fontSize="12px" color="gray.400">
          Verifique os parâmetros e tente novamente.
        </Text>
      </Flex>
    )
  }

  if (!data) return null

  return (
    <AnimatePresence>
      <motion.div
        key={animKey}
        variants={listVariants}
        initial="hidden"
        animate="visible"
      >
        <VStack spacing={6} align="stretch">
          {/* Resumo */}
          <motion.div variants={cardVariants}>
            <Box
              bg="blue.50"
              borderRadius="10px"
              border="1px solid"
              borderColor="blue.100"
              px={5}
              py={3}
            >
              <Text fontSize="12.5px" color="blue.800" lineHeight="1.7">
                {data.memoriaCalculo.resumo}
              </Text>
            </Box>
          </motion.div>

          {/* Stats */}
          <Grid
            templateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(4, 1fr)']}
            gap={3}
          >
            <StatCard
              label="Venda Média Diária"
              value={`${formatNumber(data.totais.vendaMediaDiaria)} un/dia`}
              color={BRAND_GREEN}
            />
            <StatCard
              label="Total Bruto"
              value={`${formatNumber(data.totais.totalVendasBruto, 0)} un`}
            />
            <StatCard
              label="Total Líquido"
              value={`${formatNumber(data.totais.totalVendasLiquido, 0)} un`}
            />
            <StatCard
              label="Janela"
              value={`${data.periodo.janelaEfetivaDeDias} dias`}
            />
          </Grid>

          {/* Período & Corte */}
          <Grid templateColumns={['1fr', '1fr', '1fr 1px 1fr']} gap={0}>
            {/* Período */}
            <Box pr={[0, 0, 5]} pb={[4, 4, 0]}>
              <motion.div
                key={`header-periodo-${animKey}`}
                variants={headerVariants}
                initial="hidden"
                animate="visible"
              >
                <Flex align="center" gap={3} mb={3}>
                  <Flex
                    w="36px"
                    h="36px"
                    borderRadius="9px"
                    bg="#EBF8FF"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon
                      as={MdOutlineCalendarToday}
                      color="#3182CE"
                      boxSize={4}
                    />
                  </Flex>
                  <Heading fontSize="13px" fontWeight="700" color="gray.800">
                    Período
                  </Heading>
                </Flex>
              </motion.div>
              <VStack spacing={1} align="stretch" pl="48px">
                <Text fontSize="12px" color="gray.600">
                  {formatDate(data.periodo.dataInicial)} a{' '}
                  {formatDate(data.periodo.dataFinal)}
                </Text>
                <Text fontSize="12px" color="gray.600">
                  Dias com venda: <strong>{data.periodo.diasComVenda}</strong> |
                  Sem venda: <strong>{data.periodo.diasSemVenda}</strong>
                </Text>
                <Text fontSize="11px" color="gray.400">
                  1a entrada:{' '}
                  {formatDate(data.periodo.primeiraEntradaDoProduto)}
                </Text>
              </VStack>
            </Box>

            <Box display={['none', 'none', 'block']}>
              <Divider orientation="vertical" borderColor="gray.200" />
            </Box>
            <Divider display={['block', 'block', 'none']} my={3} />

            {/* Corte */}
            <Box pl={[0, 0, 5]}>
              <motion.div
                key={`header-corte-${animKey}`}
                variants={headerVariants}
                initial="hidden"
                animate="visible"
              >
                <Flex align="center" gap={3} mb={3}>
                  <Flex
                    w="36px"
                    h="36px"
                    borderRadius="9px"
                    bg="#FFF5F5"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon as={MdOutlineFilterAlt} color="#E53E3E" boxSize={4} />
                  </Flex>
                  <Heading fontSize="13px" fontWeight="700" color="gray.800">
                    Corte de Outliers
                  </Heading>
                </Flex>
              </motion.div>
              <VStack spacing={1} align="stretch" pl="48px">
                <Text fontSize="12px" color="gray.600">
                  Percentil: <strong>{data.corte.percentil * 100}%</strong> |
                  Limite:{' '}
                  <strong>
                    {formatNumber(data.corte.limiteOutlier)} un/dia
                  </strong>
                </Text>
                <Text fontSize="12px" color="gray.600">
                  Dias excluídos: <strong>{data.corte.diasExcluidos}</strong> |
                  Volume excluído:{' '}
                  <strong>
                    {formatNumber(data.corte.volumeExcluido, 0)} un
                  </strong>
                </Text>
                <Text fontSize="11px" color="gray.400">
                  Guard atingido: {data.corte.guardAtingido ? 'Sim' : 'Não'} |
                  Rede de segurança:{' '}
                  {data.corte.redeSegurancaAcionada ? 'Sim' : 'Não'}
                </Text>
              </VStack>
            </Box>
          </Grid>

          {/* Memória de Cálculo */}
          <Box>
            <motion.div
              key={`header-passos-${animKey}`}
              variants={headerVariants}
              initial="hidden"
              animate="visible"
            >
              <Flex align="center" gap={3} mb={4}>
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="10px"
                  bg="#EBF8FF"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={MdOutlineInsights} color="#3182CE" boxSize={5} />
                </Flex>
                <Box>
                  <Heading fontSize="14px" fontWeight="700" color="gray.800">
                    Memória de Cálculo
                  </Heading>
                  <Text fontSize="11px" color="gray.500" mt="1px">
                    Etapas do cálculo da Venda Média Móvel
                  </Text>
                </Box>
              </Flex>
            </motion.div>

            <VStack spacing={2} align="stretch">
              {data.memoriaCalculo.passos.map((passo, i) => (
                <VmmStepCard
                  key={passo.ordem}
                  passo={passo}
                  index={i}
                  total={data.memoriaCalculo.passos.length}
                />
              ))}
            </VStack>
          </Box>

          {/* Tabela de Dias */}
          {data.dias && data.dias.length > 0 && (
            <Box>
              <motion.div
                key={`header-dias-${animKey}`}
                variants={headerVariants}
                initial="hidden"
                animate="visible"
              >
                <Flex align="center" gap={3} mb={4}>
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="10px"
                    bg="#F0FFF4"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon as={FiTrendingUp} color={BRAND_GREEN} boxSize={5} />
                  </Flex>
                  <Box>
                    <Heading fontSize="14px" fontWeight="700" color="gray.800">
                      Vendas Diárias
                    </Heading>
                    <Text fontSize="11px" color="gray.500" mt="1px">
                      Detalhamento dia a dia dentro da janela
                    </Text>
                  </Box>
                </Flex>
              </motion.div>

              <Box
                maxH="300px"
                overflowY="auto"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="10px"
              >
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                    <Tr>
                      <Th fontSize="11px">Data</Th>
                      <Th fontSize="11px" isNumeric>
                        Quantidade
                      </Th>
                      <Th fontSize="11px">Situação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.dias.map((dia, i) => {
                      const excluido = dia.situacao === 'Excluido'
                      return (
                        <Tr
                          key={i}
                          bg={excluido ? 'red.50' : 'white'}
                          opacity={excluido ? 0.75 : 1}
                        >
                          <Td fontSize="12px">{formatDate(dia.data)}</Td>
                          <Td fontSize="12px" isNumeric>
                            {formatNumber(dia.quantidade)}
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={excluido ? 'red' : 'green'}
                              variant="subtle"
                              fontSize="10px"
                            >
                              {dia.situacao}
                            </Badge>
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </VStack>
      </motion.div>
    </AnimatePresence>
  )
}

// ── Modal principal com Tabs ─────────────────────────────────────────────────

const CalculoDetalheTabsModal: FC<CalculoDetalheTabsModalProps> = ({
  isOpen,
  onClose,
  produtoId,
  lojaId,
  etapasSaida,
  etapasQuantidade,
  defaultTab,
  showVmmTab
}) => {
  const [tabIndex, setTabIndex] = useState(defaultTab === 'vmm' ? 0 : 1)

  useEffect(() => {
    if (isOpen) setTabIndex(defaultTab === 'vmm' ? 0 : 1)
  }, [isOpen, defaultTab])

  const animKey = isOpen ? `open-${produtoId}-${lojaId}` : 'closed'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
      size="6xl"
    >
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.400" />
      <ModalContent
        bg="white"
        borderRadius="16px"
        overflow="hidden"
        mx={[4, 8]}
        maxH="90vh"
      >
        {/* Header */}
        <ModalHeader
          p={0}
          style={{
            background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #2a7a58 100%)`
          }}
        >
          <Flex align="center" px={5} py={4} gap={3}>
            <Flex
              w="38px"
              h="38px"
              borderRadius="9px"
              bg="whiteAlpha.200"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon as={BsCalculator} color="white" boxSize={5} />
            </Flex>
            <Box>
              <Heading fontSize="15px" color="white" fontWeight="700">
                Detalhamento do Cálculo
              </Heading>
              <Flex align="center" gap={2} mt="2px">
                <Badge
                  bg="whiteAlpha.300"
                  color="white"
                  fontSize="10px"
                  px={2}
                  borderRadius="full"
                  fontWeight="600"
                >
                  Loja {lojaId}
                </Badge>
                <Badge
                  bg={BRAND_YELLOW}
                  color={BRAND_GREEN}
                  fontSize="10px"
                  px={2}
                  borderRadius="full"
                  fontWeight="700"
                >
                  Produto {produtoId}
                </Badge>
              </Flex>
            </Box>
          </Flex>
          <Box h="3px" bg={BRAND_YELLOW} />
        </ModalHeader>

        <ModalCloseButton
          top={3}
          right={3}
          color="white"
          size="sm"
          _hover={{ bg: 'whiteAlpha.300' }}
          zIndex={10}
        />

        {/* Body */}
        <ModalBody p={0} overflowY="auto">
          {showVmmTab ? (
            <Tabs
              index={tabIndex}
              onChange={setTabIndex}
              variant="enclosed"
              colorScheme="green"
            >
              <TabList
                px={6}
                pt={4}
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Tab
                  fontSize="13px"
                  fontWeight="600"
                  _selected={{
                    color: BRAND_GREEN,
                    borderColor: 'gray.200',
                    borderBottom: '2px solid',
                    borderBottomColor: BRAND_GREEN
                  }}
                >
                  Venda Média Móvel
                </Tab>
                <Tab
                  fontSize="13px"
                  fontWeight="600"
                  _selected={{
                    color: '#3182CE',
                    borderColor: 'gray.200',
                    borderBottom: '2px solid',
                    borderBottomColor: '#3182CE'
                  }}
                >
                  Cálculo da Projeção
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={6}>
                  <VmmTabContent
                    produtoId={produtoId}
                    lojaId={lojaId}
                    isActive={isOpen && tabIndex === 0}
                    animKey={`vmm-${animKey}`}
                  />
                </TabPanel>
                <TabPanel p={6}>
                  <LambdaTabContent
                    etapasSaida={etapasSaida}
                    etapasQuantidade={etapasQuantidade}
                    animKey={`lambda-${animKey}`}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <Box p={6}>
              <LambdaTabContent
                etapasSaida={etapasSaida}
                etapasQuantidade={etapasQuantidade}
                animKey={`lambda-${animKey}`}
              />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default CalculoDetalheTabsModal
